import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChipRow, EmptyState, Pill, Spinner } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Plus, Search } from "lucide-react";
import { formatDate } from "../lib/dates";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "sampling", label: "Sampling" },
  { key: "production", label: "Production" },
];

export default function Quotes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (type !== "all") sp.set("quote_type", type);
    try {
      const rows = await api.get(`/quotes?${sp}`);
      // Initial-meeting docs have their own dedicated section — hide them here.
      setItems(rows.filter((q) => q.quote_type !== "initial_meeting"));
    } finally { setLoading(false); }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const dateStr = it.created_at ? String(it.created_at).slice(0, 10) : "";
      // Match ISO (YYYY-MM-DD), dd/mm/yyyy, or partial forms
      const dateDisplay = formatDate(dateStr).toLowerCase();
      return (
        (it.customer_name || "").toLowerCase().includes(q) ||
        (it.brand_name || "").toLowerCase().includes(q) ||
        (it.quote_number || "").toLowerCase().includes(q) ||
        (it.title || "").toLowerCase().includes(q) ||
        (it.status || "").toLowerCase().includes(q) ||
        dateStr.includes(q) ||
        dateDisplay.includes(q)
      );
    });
  }, [items, query]);

  const tone = (st) => st === "paid" || st === "approved" ? "success" : st === "rejected" ? "danger" : st === "sent" ? "brand" : "neutral";

  return (
    <>
      <AppHeader title="Quotes" />
      <div className="sg-main sg-fade-in">
        <div style={{ position: "relative", marginBottom: 12 }}>
          <Search size={16} color="var(--muted)" style={{ position: "absolute", left: 14, top: 15 }} />
          <input
            className="sg-input"
            placeholder="Search by customer, number or date"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 40 }}
            data-testid="quotes-search"
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <ChipRow items={FILTERS} value={type} onChange={setType} testIdPrefix="quotes-type" />
        </div>

        {loading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState
            title={query ? `No matches for "${query}"` : "No quotes yet"}
            hint={query ? "Try a different name, number or date." : "Tap + to create a new quote."}
          />
        ) : (
          filtered.map((q) => (
            <div key={q.quote_id} className="sg-list-card" onClick={() => navigate(`/quotes/${q.quote_id}`)} data-testid={`quote-row-${q.quote_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brand)", letterSpacing: 0.4 }}>{q.quote_number}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.customer_name}</div>
                  {q.created_at && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{formatDate(String(q.created_at).slice(0, 10))}</div>}
                </div>
                <Pill label={q.status} tone={tone(q.status)} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={tag}>{String(q.quote_type).replace("_", " ")}</span>
                <span style={{ fontSize: 16, fontWeight: 800 }}>₹{Number(q.amount).toLocaleString("en-IN")}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <button className="sg-fab" onClick={() => navigate("/quotes/new")} data-testid="quotes-add-button" aria-label="New quote">
        <Plus size={24} />
      </button>
    </>
  );
}

const tag = { background: "var(--surface-subtle)", color: "var(--muted)", padding: "3px 8px", borderRadius: 6, fontSize: 11, textTransform: "capitalize" };
